import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast, Button } from 'antd-mobile';
import { inApp } from '@hst/utils';
import { getAccountStatus, getRedeemCode } from './service';
import { FailTypeEnum, URLS, jumpToH5 } from './utils';
import openRewardModal from './modal/reward';
import openErrorModal from './modal/error';
import openLoginModal, { goLogin } from './modal/login';

import './index.less';
import { trackRedeemBtnClick, trackRedeemCompView } from '../../track';

type Props = {
	data: any;
};

const CODE_MIN_LENGTH = 3;
const CODE_MAX_LENGTH = 20;

const Index = (props: Props) => {
	useEffect(() => {
		trackRedeemCompView();
	}, []);

	const { data } = props;
	const [btnLoading, setBtnLoading] = useState(false);

	const toRedeemRecord = async () => {
		// 跳转到兑换记录
		const info = await getLoginInfo();
		if (!info) {
			goLogin(i18n.language);
			return;
		}
		const url = URLS.Redeem[process.env.REACT_APP_ENV];
		const params = {
			lang: i18n.language?.replace('_', '-'),
			_app_fullScreen: 'y',
		};
		jumpToH5(
			url,
			inApp()
				? {
						...params,
						isBack: 'yes',
					}
				: params,
		);
	};

	const toTc = () => {
		// 跳转到兑换规则
		const url = URLS.Help[process.env.REACT_APP_ENV];
		jumpToH5(url, {
			_app_fullScreen: 'y',
		});
	};

	const { i18n, t } = useTranslation();
	const descTitle = `*${t('redeem.tooltip_title')}`; // '*口令红包领取说明：',

	const descContent = [
		t('redeem.tooltip_content_1'), // '口令必须在其有效期内使用。',
		t('redeem.tooltip_content_2'), // '口令不区分大小写。请输入正确的代码进行兑换。',
		t('redeem.tooltip_content_3'), // '每个口令只能使用一次，并且与口令相对应的奖励数量有限，先到先得。',
		t('redeem.tooltip_content_4'), // '口令不与实名注册关联。请妥善保管，M+ Global 对任何丢失概不负责。',
		t('redeem.tooltip_content_5'), // '兑换成功后，您可以查看兑换奖品的历史记录。',
		t('redeem.tooltip_content_6'), // '奖品由 M+ Global 提供，与 Apple Inc. 无关。',
	];
	const moreDesc = [
		t('redeem.tooltip_content_7'), // '更多详情，请查阅',
		t('redeem.tooltip_content_9'), // 'T&C。',
		t('redeem.tooltip_content_8'), // '点此处查看奖品兑换历史记录。'
	];
	const stepList = [
		{
			step: 1,
			desc: t('redeem.step_1'), // 'Dapat kod laluan'
		},
		{
			step: 2,
			desc: t('redeem.step_2'), // 'Masukkan'
		},
		{
			step: 3,
			desc: t('redeem.step_3'), // 'Dapat ganjaran'
		},
	];

	const [code, setCode] = useState('');

	const onCodeChange = (e: any) => {
		const value = e.target.value;
		// 只保留数字和字母
		const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, '');
		// 字母转大写
		const uppercaseValue = alphanumericValue.toUpperCase();
		setCode(uppercaseValue);
	};

	const buttonDisabled = useMemo(() => {
		return code?.length < CODE_MIN_LENGTH;
	}, [code]);

	const getLoginInfo = async () => {
		const res = await getAccountStatus({
			buzz: {
				withAC: true,
			},
		});
		const {
			data: { success, data },
		} = res;
		if (success && data?.logined) {
			// dispatch('user/setUserInfo', data);
			return data;
		}
	};

	const getErrorMessageByFailType = (failType) => {
		switch (failType) {
			case FailTypeEnum.SDK_ABSENT:
				return t('redeem.errors_28'); // 兑换失败，不存在此兑换码，请重新输入
			case FailTypeEnum.SDK_INVALID:
			case FailTypeEnum.REDEMPTION_SYSTEM_ERROR:
				return t('redeem.errors_29'); // 兑换失败，请再次尝试
			case FailTypeEnum.CODE_NOT_STARTED:
				return t('redeem.errors_23_1'); // 兑换失败，本次活动还未开始
			case FailTypeEnum.CODE_ENDED:
				return t('redeem.errors_23_2'); // 兑换失败，本次活动已结束
			case FailTypeEnum.SINGLE_CODE_LIMIT:
				return t('redeem.errors_27'); // 兑换失败，您已参与本活动
			case FailTypeEnum.MULTI_CODE_LIMIT:
				return t('redeem.errors_21'); // 兑换失败，今日兑换已到上限，请明天再尝试
			case FailTypeEnum.SDK_COUNT_LIMIT:
				return t('redeem.errors_22'); // 兑换失败，该码已被使用
			case FailTypeEnum.USER_TYPE_LIMIT:
			case FailTypeEnum.CHANNEL_LIMIT:
			case FailTypeEnum.CODE_ID_MUTEX:
			case FailTypeEnum.CODE_BUZ_TYPEMUTEX:
				return t('redeem.errors_26'); // 兑换失败，您不在本次活动范畴内
			default:
				return t('redeem.errors_29'); // 兑换失败，请再次尝试
		}
	};

	const handleReward = async () => {
		try {
			const {
				data: { success, data, msg, error },
			} = await getRedeemCode({
				sdkCode: code,
				inlet: '2', // 核销来源(非必填); 1:个人中心兑换;2:API兑换
			});

			if (success) {
				// 展示奖励
				const { rewards = [], failType } = data || {};
				if (rewards && rewards.length > 0) {
					const rewardsList = rewards?.map((res) => res.rewardName) || [];
					openRewardModal({ rewardsList, t });
				} else {
					const errorText = getErrorMessageByFailType(failType);
					openErrorModal({ errorText, t });
				}
			} else {
				const errorText = msg || error || getErrorMessageByFailType(12);
				openErrorModal({ errorText, t });
			}
		} catch (error) {
			Toast.show(JSON.stringify(error));
		}
	};

	const onRcClick = async () => {
		trackRedeemBtnClick();
		setBtnLoading(true);
		try {
			const info = await getLoginInfo();
			if (!info) {
				const lang = i18n.language;
				openLoginModal(t, lang);
				return;
			}
			// 跳转到兑换记录
			handleReward();
		} catch (error) {
			console.log(error);
		} finally {
			setBtnLoading(false);
		}
	};

	return (
		<div className='rc-container'>
			<div className='rc-card'>
				<div className='rc-card-hd'>
					<div className='rc-card--title'>{data?.redeemCodeTitle}</div>
				</div>
				<div className='rc-card-bd'>
					<div className='rc-input'>
						<input
							placeholder={t('redeem.input_placeholder')}
							onChange={onCodeChange}
							value={code}
							maxLength={CODE_MAX_LENGTH}
						/>
					</div>
					<div>
						<Button
							className='rc-btn'
							style={{ backgroundColor: data.redeemCodeBtnColor }}
							disabled={buttonDisabled}
							onClick={onRcClick}
							loading={btnLoading}>
							{data.redeemCodeBtnName}
						</Button>
					</div>
					<div className='rc-stepper'>
						{stepList.map((item) => (
							<div key={item.step} className='rc-stepper-item'>
								<div className='rc-stepper--counter'>{item.step}</div>
								<div className='rc-stepper--name'>{item.desc}</div>
							</div>
						))}
					</div>
				</div>
				<div className='rc-card-ft'>
					<div className='rc-rule'>
						<p>{descTitle}</p>
						<ol className='rc-rule-list'>
							{descContent.map((item, index) => (
								<li key={index}>{item}</li>
							))}

							<li>
								{moreDesc[0]}
								<a className='rc-link' href='javascript:;' onClick={toTc}>
									{moreDesc[1]}
								</a>
							</li>
							<li>
								<a className='rc-link' href='javascript:;' onClick={toRedeemRecord}>
									{moreDesc[2]}
								</a>
							</li>
						</ol>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Index;
